import { useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa"; // Ajout des icônes pour afficher/masquer le mot de passe
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css"; // Importer les styles de la bibliothèque

const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;


function SignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({
    length: true,
    specialChar: true,
    casing: true,
    number: true,
  });

  // Fonction de validation du mot de passe
  const validatePassword = (password) => {
    const errors = {
      length: password.length < 8,
      specialChar: !/[!@#$%^&*(),.?":{}|<>_-]/g.test(password),
      casing: !(/[A-Z]/.test(password) && /[a-z]/.test(password)),
      number: !/\d/.test(password),
    };
    setPasswordErrors(errors);
    return !Object.values(errors).some(error => error); // Renvoie true si le mot de passe est valide
  };

  // Gestion des changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validation du mot de passe pendant la saisie
    if (name === "password") {
      validatePassword(value);
    }
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Réinitialiser les notifications avant chaque soumission
    toast.dismiss();

    // Vérification de la validité du mot de passe
    if (!validatePassword(formData.password)) {
      toast.error("Le mot de passe ne répond pas aux critères de sécurité.");
      return;
    }

    // Vérification de la correspondance des mots de passe
    if (formData.password !== formData.confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${USER_BACKEND_API_BASE_URL}/users/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          password: formData.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        toast.error(errorData.error || "Erreur lors de la création du compte.");
        setLoading(false);
        return;
      }

      // En cas de succès, afficher une notification et rediriger vers la page de connexion
      toast.success("Compte créé avec succès ! Vous pouvez maintenant vous connecter.");

      // Optionnel : rediriger après un délai pour laisser le message visible
      setTimeout(() => {
        navigate("/login");
      }, 2000);

    } catch (err) {
      console.error("Erreur :", err);
      toast.error("Une erreur est survenue. Veuillez réessayer.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4">
      <div className="bg-white p-8 rounded shadow-md w-full max-w-md">
        <h2 className="text-2xl font-bold mb-6 text-center">Créer mon compte</h2>
        <form onSubmit={handleSubmit}>
          {/* Champ Nom */}
          <div className="mb-4">
            <label htmlFor="nom" className="block text-gray-700 mb-1">
              Nom
            </label>
            <input
              type="text"
              name="nom"
              id="nom"
              value={formData.nom}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded"
              required
            />
          </div>
          {/* Champ Prénom */}
          <div className="mb-4">
            <label htmlFor="prenom" className="block text-gray-700 mb-1">
              Prénom
            </label>
            <input
              type="text"
              name="prenom"
              id="prenom"
              value={formData.prenom}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded"
              required
            />
          </div>
          {/* Champ Email */}
          <div className="mb-4">
            <label htmlFor="email" className="block text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded"
              required
            />
          </div>
          {/* Champ Téléphone */}
          <div className="mb-4">
            <label htmlFor="telephone" className="block text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="tel"
              name="telephone"
              id="telephone"
              value={formData.telephone}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded"
              required
            />
          </div>
          {/* Champ Mot de passe */}
          <div className="mb-4">
            <label htmlFor="password" className="block text-gray-700 mb-1">
              Mot de passe
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                id="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2 rounded"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2 text-gray-600 hover:text-gray-900"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>

            {/* Indicateurs de validation du mot de passe */}
            <div className="mt-2 text-sm text-gray-600">
              <p className="font-medium mb-1">Votre mot de passe doit :</p>
              <ul className="space-y-1">
                <li className={`flex items-center ${passwordErrors.length ? 'text-red-500' : 'text-green-500'}`}>
                  <span className="mr-2">{passwordErrors.length ? '❌' : '✓'}</span>
                  Contenir au moins 8 caractères
                </li>
                <li className={`flex items-center ${passwordErrors.specialChar ? 'text-red-500' : 'text-green-500'}`}>
                  <span className="mr-2">{passwordErrors.specialChar ? '❌' : '✓'}</span>
                  Inclure au moins un caractère spécial (!, @, #, etc.)
                </li>
                <li className={`flex items-center ${passwordErrors.casing ? 'text-red-500' : 'text-green-500'}`}>
                  <span className="mr-2">{passwordErrors.casing ? '❌' : '✓'}</span>
                  Contenir des lettres majuscules et minuscules
                </li>
                <li className={`flex items-center ${passwordErrors.number ? 'text-red-500' : 'text-green-500'}`}>
                  <span className="mr-2">{passwordErrors.number ? '❌' : '✓'}</span>
                  Contenir au moins un chiffre
                </li>
              </ul>
            </div>
          </div>
          {/* Champ Confirmation mot de passe */}
          <div className="mb-6">
            <label htmlFor="confirmPassword" className="block text-gray-700 mb-1">
              Confirmer le mot de passe
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? "text" : "password"}
                name="confirmPassword"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2 rounded"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-2 text-gray-600 hover:text-gray-900"
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
            {/* Message de confirmation si les mots de passe correspondent */}
            {formData.password && formData.confirmPassword && (
              <div className="mt-1 text-sm">
                {formData.password === formData.confirmPassword ? (
                  <span className="text-green-500">✓ Les mots de passe correspondent</span>
                ) : (
                  <span className="text-red-500">❌ Les mots de passe ne correspondent pas</span>
                )}
              </div>
            )}
          </div>
          <button
            type="submit"
            className="w-full bg-[#00675A] text-white py-2 rounded hover:bg-[#00584e] transition-colors"
            disabled={loading}
          >
            {loading ? "Création en cours..." : "Créer mon compte"}
          </button>
        </form>
        <div className="mt-4 text-center text-gray-600 text-sm">
          Vous avez déjà un compte ?{" "}
          <span
            onClick={() => navigate("/login")}
            className="text-[#00675A] cursor-pointer hover:underline"
          >
            Se connecter
          </span>
        </div>
      </div>

      {/* Conteneur pour les notifications */}
      <ToastContainer />
    </div>
  );
}

export default SignupPage;